import "./navbar.scss";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import { DarkModeContext } from "../../context/darkModeContext";
import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getUserNotifications, markNotificationAsRead, subscribeToNotifications } from "../../firebase/notificationService";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button } from '@mui/material';

const Navbar = () => {
  const { dispatch } = useContext(DarkModeContext);
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMounted, setIsMounted] = useState(true);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const navigate = useNavigate();
  const profileRef = useRef();
  const notificationRef = useRef();

  // Set mounted state
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Fetch notifications
  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = subscribeToNotifications(user.uid, (notifications) => {
        if (isMounted) {
          setNotifications(notifications);
          const unread = notifications.filter(n => !n.read).length;
          setUnreadCount(unread);
        }
      });
      return () => unsubscribe && unsubscribe();
    }
  }, [user?.uid, isMounted]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification) => {
    if (!isMounted) return; // Prevent updates if component is unmounted
    
    try {
      if (!notification.read) {
        await markNotificationAsRead(notification.id);
      }
      if (notification.type === 'message' && notification.data && notification.data.senderId) {
        navigate(`/message-center/${notification.data.senderId}`);
        setShowNotifications(false);
        return;
      }
      setSelectedNotification(notification);
      setNotificationDialogOpen(true);
      setShowNotifications(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return '💬';
      case 'payment':
        return '💰';
      case 'maintenance':
        return '🔧';
      case 'notice':
        return '📢';
      case 'penalty':
        return '⚠️';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'message':
        return '#007bff';
      case 'payment':
        return '#28a745';
      case 'maintenance':
        return '#ffc107';
      case 'notice':
        return '#17a2b8';
      case 'penalty':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };



  return (
    <>
      <div className="navbar">
        <div className="wrapper">
          <div className="items">
            <div
                className="item"
                ref={notificationRef}
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowProfileMenu(false);
                }}
                style={{ position: "relative" }}
            >
              <NotificationsNoneOutlinedIcon className="icon" />
              {unreadCount > 0 && (
                <div className="counter">{unreadCount > 99 ? '99+' : unreadCount}</div>
              )}
              {showNotifications && (
                  <div className="notifications-popup">
                    <div className="notifications-header">
                      <h4>Notifications</h4>
                      {notifications.length > 0 && (
                        <span className="notification-count">{notifications.length}</span>
                      )}
                    </div>
                    <div className="notifications-list">
                      {notifications.length === 0 ? (
                        <div className="no-notifications">
                          <p>No notifications</p>
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((notification) => (
                          <div 
                            key={notification.id} 
                            className={`notification-item ${!notification.read ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="notification-icon" style={{ color: getNotificationColor(notification.type) }}>
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="notification-content">
                              <div className="notification-title">{notification.title}</div>
                              <div className="notification-message">{notification.message}</div>
                              <div className="notification-time">
                                {notification.createdAt && (
                                  (() => {
                                    try {
                                      // Firestore Timestamp object
                                      const date = typeof notification.createdAt === 'object' && notification.createdAt.seconds
                                        ? new Date(notification.createdAt.seconds * 1000)
                                        : new Date(notification.createdAt);
                                      if (isNaN(date.getTime())) return 'N/A';
                                      return formatDistanceToNow(date, { addSuffix: true });
                                    } catch {
                                      return 'N/A';
                                    }
                                  })()
                                )}
                              </div>
                            </div>
                            {!notification.read && <div className="unread-dot"></div>}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="notifications-footer" style={{ cursor: 'pointer', textAlign: 'center', padding: '8px 0', color: '#f3678c', fontWeight: 600 }} onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>
                      View all notifications
                    </div>
                  </div>
              )}
            </div>

            {/* Profile Avatar and Dropdown */}
            <div className="item" ref={profileRef} style={{ position: "relative" }}>
              <img
                  src="https://images.pexels.com/photos/941693/pexels-photo-941693.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=500"
                  alt=""
                  className="avatar"
                  onClick={() => {
                    setShowProfileMenu(!showProfileMenu);
                    setShowNotifications(false);
                  }}
                  style={{ cursor: "pointer" }}
              />
              {showProfileMenu && (
                  <div className="profile-dropdown">
                    <div className="dropdown-item" onClick={() => navigate("/profile")}>Edit Profile</div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Details</DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <>
              <Typography variant="h6" gutterBottom>{selectedNotification.title}</Typography>
              <Typography variant="body1" gutterBottom>{selectedNotification.message}</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedNotification.createdAt && (typeof selectedNotification.createdAt === 'object' && selectedNotification.createdAt.seconds
                  ? new Date(selectedNotification.createdAt.seconds * 1000).toLocaleString()
                  : new Date(selectedNotification.createdAt).toLocaleString())}
              </Typography>
              <Typography variant="body2" color="textSecondary">Status: {selectedNotification.read ? 'Read' : 'Unread'}</Typography>
              {/* Show E-Wallet receipt image if present */}
              {selectedNotification.data && selectedNotification.data.receiptUrl && (
                <div style={{ marginTop: 16 }}>
                  <Typography variant="body2">E-Wallet Receipt:</Typography>
                  <img
                    src={selectedNotification.data.receiptUrl}
                    alt="E-Wallet Receipt"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: 8
                    }}
                  />
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
